import axios from 'axios';

/**
 * Member Lab Service
 * ดึงข้อมูล Lab Results จาก MFU API (เส้นเดียวกับ mfu-admin)
 * ใช้ API: https://mfuwellnesscenter-server.dietz.asia/api/mfu-proxy/member-lab-list
 */

const API_BASE_URL = 'https://mfuwellnesscenter-server.dietz.asia';

/**
 * ดึง NID ของ user ที่ login อยู่
 */
const getUserNID = () => {
  try {
    const username = localStorage.getItem('username');
    if (username) return username;

    const userData = localStorage.getItem('userData');
    if (userData) {
      const data = JSON.parse(userData);
      return data?.id || data?.nid || null;
    }
    return null;
  } catch (error) {
    console.error('Error getting user NID:', error);
    return null;
  }
};

/**
 * ดึงข้อมูล Lab Results ทั้งหมดของ user จาก MFU API
 * @returns {Promise} ข้อมูล lab results ในรูปแบบที่แปลงแล้ว
 */
export const getMemberLabData = async () => {
  try {
    const userNID = getUserNID();
    if (!userNID) {
      console.warn('⚠️ No user NID found');
      return null;
    }

    console.log('🔍 Fetching lab data for NID:', userNID);

    // ใช้ช่วงวันที่กว้างๆ เพื่อดึงข้อมูลทั้งหมด
    const today = new Date();
    const fiveYearsAgo = new Date();
    fiveYearsAgo.setFullYear(today.getFullYear() - 5);

    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const filters = {
      dateStart: formatDate(fiveYearsAgo),
      dateStop: formatDate(today),
      keywordName: userNID // ค้นหาด้วย NID
    };

    const response = await axios.post(`${API_BASE_URL}/api/mfu-proxy/member-lab-list`, filters, {
      headers: {
        'Content-Type': 'application/json'
      }
    });

    console.log('✅ Member Lab API Response:', response.data);

    if (response.data?.result && Array.isArray(response.data.list)) {
      // แปลงข้อมูลให้อยู่ในรูปแบบที่ใช้งานง่าย
      const formattedData = formatLabData(response.data.list, userNID);
      return formattedData;
    }

    return null;
  } catch (error) {
    console.error('❌ Error fetching member lab data:', error);
    return null;
  }
};

/**
 * แปลงข้อมูล Lab จาก API ให้อยู่ในรูปแบบที่ใช้งานง่าย
 */
const formatLabData = (labList, userNID) => {
  // หา record ของ user จาก list
  const userRecords = labList.filter(record => {
    const profile = record.profile || {};
    // เช็คจาก NID หรือชื่อ
    return profile.nid === userNID ||
           profile.idCard === userNID ||
           (profile.firstname && profile.lastname);
  });

  if (userRecords.length === 0) {
    console.warn('⚠️ No records found for user');
    return null;
  }

  // รวมข้อมูลทั้งหมดจากทุก visit
  const allLabData = {};
  const visitDates = [];

  userRecords.forEach(record => {
    const dataLab = record.data_lab || [];
    const visitDate = record.profile?.visitDate || record.visitDate;

    if (visitDate) {
      visitDates.push(visitDate);
    }

    // แปลง data_lab array เป็น object
    dataLab.forEach(item => {
      if (item.name && item.value && item.value.trim() !== '') {
        const originalName = item.name;
        const name = normalizeLabName(item.name);

        // ข้าม field ที่เป็น normal value, unit, หรือ metadata
        const skipKeywords = [
          'normalvalue', 'normalval', 'normal_value', 'normal', 'unit',
          'note', 'report', 'doctor', 'nameprefix', 'sex', 'impression'
        ];
        const lowerOriginal = originalName.toLowerCase();

        // ถ้า originalName มีคำว่า normal value หรือ unit ให้ข้าม
        if (skipKeywords.some(keyword => lowerOriginal.includes(keyword))) {
          return; // skip this item
        }

        // ข้ามค่าที่ดูเหมือน range หรือ unit (มี = หรือมี - ระหว่างตัวเลข)
        const value = item.value.trim();

        // ข้ามค่าที่เป็น unit หรือ range
        if (
          value.includes('=') ||
          /^[A-Z]+=/.test(value) || // เช่น "M=0.67-1.17"
          /^M[0-9]/.test(value) || // เช่น "M12.0-18.0,F12.0-16.0"
          /^Below/.test(value) || // เช่น "Below50"
          value === 'mg/dL' ||
          value === 'g/dL' ||
          value === 'U/L' ||
          value === 'cells/HPF' ||
          value === 'cells/cumm' ||
          value === 'cell/cumm' ||
          value === '/HPF' ||
          value === '/LPF' ||
          value === '%' ||
          value === 'fl' ||
          value === 'pg' ||
          value === '-' ||
          value === 'Neg' ||
          // ข้าม "Negative" เฉพาะถ้าเป็น normal value field
          (value === 'Negative' && originalName.toLowerCase().includes('normal')) ||
          // ข้าม range ของตัวเลข แต่ต้องไม่ใช่ค่าจริงเช่น "1-2" หรือ "0-1"
          /^[0-9]{3,}-[0-9]{3,}$/.test(value) // เช่น "5000-10000" (3 หลักขึ้นไป)
        ) {
          return; // skip
        }

        // Debug: แสดงการ map ชื่อ
        const debugParams = ['creatinine', 'bun', 'egfr', 'wbc', 'hemoglobin', 'hematocrit', 'platelet', 'mcv', 'mch', 'mchc', 'sgot', 'sgpt', 'alp', 'urinalysis_ph', 'urinalysis_color', 'urinalysis_protein', 'urinalysis_wbc', 'urinalysis_rbc'];
        if (debugParams.includes(name)) {
          console.log(`🔍 Normalized: "${originalName}" → "${name}" = "${value}"`);
        }

        if (!allLabData[name]) {
          allLabData[name] = [];
        }

        // จัดการค่าพิเศษ
        let cleanValue = value;

        // สำหรับ eGFR ที่มี format เช่น "111.8[Stage1]" ให้เอาเฉพาะตัวเลข
        if (name === 'egfr' && cleanValue.includes('[')) {
          cleanValue = cleanValue.split('[')[0].trim();
        }

        // สำหรับค่าที่มี comma (เช่น "6,110" หรือ "324,000") ให้ลบ comma ออก
        if (typeof cleanValue === 'string' && cleanValue.includes(',')) {
          cleanValue = cleanValue.replace(/,/g, '');
        }

        // ถ้ามีค่าซ้ำกันในวันเดียวกัน ให้เก็บเฉพาะค่าแรก (ป้องกัน duplicate field names)
        const isDuplicate = allLabData[name].some(existing =>
          existing.date === (visitDate || new Date().toISOString()) &&
          existing.value === cleanValue
        );

        if (!isDuplicate) {
          allLabData[name].push({
            value: cleanValue,
            date: visitDate || new Date().toISOString(),
            unit: item.unit || ''
          });
        }
      }
    });
  });

  // เรียงลำดับแต่ละ parameter ตามวันที่ (ล่าสุดก่อน)
  Object.keys(allLabData).forEach(key => {
    allLabData[key].sort((a, b) => new Date(b.date) - new Date(a.date));
  });

  // Debug: แสดงจำนวนค่าแต่ละ parameter
  console.log('📊 Lab Data Summary:');
  Object.keys(allLabData).forEach(key => {
    if (allLabData[key].length > 0) {
      console.log(`  - ${key}: ${allLabData[key].length} value(s)`);
    }
  });

  // ดึง profile จาก record ล่าสุด
  const latestRecord = userRecords[0];
  const profile = latestRecord?.profile || {};

  // ดึง height และ weight จาก labData (ค่าล่าสุด)
  const heightFromLab = allLabData['height']?.[0]?.value || profile.height || 0;
  const weightFromLab = allLabData['weight']?.[0]?.value || profile.weight || 0;

  return {
    profile: {
      nid: profile.nid || profile.idCard || userNID,
      firstname: profile.firstname || '',
      lastname: profile.lastname || '',
      gender: normalizeGender(profile.sex || profile.gender),
      age: calculateAge(profile.birthday),
      birthday: profile.birthday,
      height: parseFloat(heightFromLab) || 0,
      weight: parseFloat(weightFromLab) || 0
    },
    labData: allLabData,
    visitDates: [...new Set(visitDates)].sort((a, b) => new Date(b) - new Date(a))
  };
};

/**
 * Normalize ชื่อ lab parameter ให้ตรงกับที่ใช้ใน app
 */
const normalizeLabName = (name) => {
  const nameMap = {
    // Kidney
    'kidneyfunctiontestcreatinine': 'creatinine',
    'kidneyfunctiontestbun': 'bun',
    'kidneyfunctiontestegfr': 'egfr',
    'creatininecreatinine': 'creatinine',
    'creatinineegfr': 'egfr',
    'creatinineegfrresult': 'egfr',
    'creatinine': 'creatinine',
    'bun': 'bun',
    'egfr': 'egfr',

    // CBC - ใช้ชื่อเต็มจาก API
    'cbcrbc': 'rbc',
    'cbchemoglobin': 'hemoglobin',
    'cbchematocrit': 'hematocrit',
    'cbcwbc': 'wbc',
    'cbcplatelet': 'platelet',
    'cbcplt': 'platelet',
    'cbcpltcount': 'platelet',
    'cbcmcv': 'mcv',
    'cbcmch': 'mch',
    'cbcmchc': 'mchc',
    'wbc': 'wbc',
    'hb': 'hemoglobin',
    'hct': 'hematocrit',
    'plt': 'platelet',
    'rbc': 'rbc',
    'hemoglobin': 'hemoglobin',
    'hematocrit': 'hematocrit',
    'platelet': 'platelet',
    'mcv': 'mcv',
    'mch': 'mch',
    'mchc': 'mchc',
    'neutrophile': 'neutrophile',
    'lymphocyte-count': 'lymphocyte-count',
    'monocyte-count': 'monocyte-count',
    'basophil-count': 'basophil-count',
    'lymphocytecount': 'lymphocyte-count',
    'monocytecount': 'monocyte-count',
    'basophilcount': 'basophil-count',

    // HbA1c
    'diabeteshba1c': 'hba1c',
    'hba1c': 'hba1c',

    // Lipid
    'lipidprofilecholesterol': 'cholesterol',
    'lipidprofilehdl': 'hdl',
    'lipidprofileldl': 'ldl',
    'lipidprofiletriglyceride': 'triglyceride',
    'cholesterol': 'cholesterol',
    'hdl': 'hdl',
    'ldl': 'ldl',
    'triglyceride': 'triglyceride',
    'tg': 'triglyceride',

    // Liver
    'liverfunctiontestsgot': 'sgot',
    'liverfunctiontestsgpt': 'sgpt',
    'liverfunctiontestalk': 'alp',
    'liverfunctiontestalkp': 'alp',
    'sgot': 'sgot',
    'sgpt': 'sgpt',
    'ast': 'sgot',
    'alt': 'sgpt',
    'alp': 'alp',
    'alkp': 'alp',
    'alkalinephosphatase': 'alp',

    // Uric Acid
    'uricacid': 'uric_acid',
    'uric_acid': 'uric_acid',

    // Blood Pressure & Sugar
    'systolicbloodpressure': 'systolic',
    'diastolicbloodpressure': 'diastolic',
    'bloodpressuresystolic': 'systolic',
    'bloodpressurediastolic': 'diastolic',
    'sys': 'systolic',
    'dia': 'diastolic',
    'fastingbloodsugar': 'sugar',
    'diabetesfbs': 'sugar',
    'fbs': 'sugar',
    'sugar': 'sugar',

    // Physical
    'weight': 'weight',
    'height': 'height',
    'bmi': 'bmi',
    'waistline': 'waistline',
    'pulse': 'pulse',
    'respiratoryrate': 'respiratory_rate',

    // Urine Test - ใช้ชื่อจาก API จริง (มีหลายรูปแบบ)
    'urinalysisphresult': 'urinalysis_ph',
    'urinalysiscolorresul': 'urinalysis_color',
    'urinalysiscolorresult': 'urinalysis_color',
    'urinalysisapperancer': 'urinalysis_appearance',
    'urinalysisapperanceresult': 'urinalysis_appearance',
    'urinalysisspecificgr': 'urinalysis_specific_gravity',
    'urinalysisspecificgrresult': 'urinalysis_specific_gravity',
    'urinalysisproteinres': 'urinalysis_protein',
    'urinalysisproteinresult': 'urinalysis_protein',
    'urinalysissugarresul': 'urinalysis_glucose',
    'urinalysissugarresult': 'urinalysis_glucose',
    'urinalysisketoneresu': 'urinalysis_ketone',
    'urinalysisketoneresult': 'urinalysis_ketone',
    'urinalysisbloodresul': 'urinalysis_blood',
    'urinalysisbloodresult': 'urinalysis_blood',
    'urinalysisbilirubinresult': 'urinalysis_bilirubin',
    'urinalysisurobilinogenresult': 'urinalysis_urobilinogen',
    'urinalysisleukocyteresult': 'urinalysis_leukocyte',
    'urinalysisnitriteresult': 'urinalysis_nitrite',
    'urinalysisrbcresult': 'urinalysis_rbc',
    'urinalysiswbcresult': 'urinalysis_wbc',
    'urinalysisph': 'urinalysis_ph',
    'urinalysiscolor': 'urinalysis_color',
    'urinalysisappearance': 'urinalysis_appearance',
    'urinalysisspecificgravity': 'urinalysis_specific_gravity',
    'urinalysisprotein': 'urinalysis_protein',
    'urinalysisglucose': 'urinalysis_glucose',
    'urinalysisketone': 'urinalysis_ketone',
    'urinalysisblood': 'urinalysis_blood',
    'urinalysisbilirubin': 'urinalysis_bilirubin',
    'urinalysisurobilinogen': 'urinalysis_urobilinogen',
    'urinalysisleukocyte': 'urinalysis_leukocyte',
    'urinalysisnitrite': 'urinalysis_nitrite',
    'urinalysisrbc': 'urinalysis_rbc',
    'urinalysiswbc': 'urinalysis_wbc',

    // Stool Test
    'stoolcolor': 'stool_color',
    'stoolconsistency': 'stool_consistency',
    'stoolrbc': 'stool_rbc',
    'stoolwbc': 'stool_wbc',
    'stoolparasite': 'stool_parasite',
    'stooloccultblood': 'stool_occult_blood'
  };

  const normalizedName = name.toLowerCase().replace(/[^a-z0-9]/g, '');
  return nameMap[normalizedName] || normalizedName;
};

/**
 * Normalize gender value
 */
const normalizeGender = (gender) => {
  if (!gender) return 'M';
  const g = gender.toString().toLowerCase();
  if (g === 'f' || g === 'female' || g === '0' || g === 'หญิง') return 'F';
  return 'M';
};

/**
 * คำนวณอายุจากวันเกิด
 */
const calculateAge = (birthday) => {
  if (!birthday) return 0;
  const birthDate = new Date(birthday);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

/**
 * Helper: ดึงค่าล่าสุดของ parameter
 */
export const getLatestValue = (labData, paramName) => {
  if (!labData || !labData[paramName] || labData[paramName].length === 0) {
    return null;
  }
  return labData[paramName][0]; // ค่าแรกคือค่าล่าสุด (เรียงลำดับแล้ว)
};

/**
 * Helper: ดึงประวัติทั้งหมดของ parameter
 */
export const getHistory = (labData, paramName) => {
  if (!labData || !labData[paramName]) {
    return [];
  }
  return labData[paramName];
};

/**
 * Helper: ดึงค่า Blood Pressure (รวม systolic/diastolic)
 */
export const getBloodPressure = (labData) => {
  const systolic = getLatestValue(labData, 'systolic');
  const diastolic = getLatestValue(labData, 'diastolic');

  if (systolic && diastolic) {
    return {
      value: `${systolic.value}/${diastolic.value}`,
      date: systolic.date
    };
  }
  return null;
};

/**
 * Helper: ดึงข้อมูล Urine Test ทั้งหมด
 */
export const getUrineTestData = (labData) => {
  const urineParams = [
    'urinalysis_ph', 'urinalysis_color', 'urinalysis_appearance',
    'urinalysis_specific_gravity', 'urinalysis_protein', 'urinalysis_glucose',
    'urinalysis_ketone', 'urinalysis_blood', 'urinalysis_bilirubin',
    'urinalysis_urobilinogen', 'urinalysis_leukocyte', 'urinalysis_nitrite',
    'urinalysis_rbc', 'urinalysis_wbc'
  ];

  const urineData = {};
  let hasData = false;

  urineParams.forEach(param => {
    const value = getLatestValue(labData, param);
    if (value) {
      urineData[param] = value.value;
      hasData = true;
    }
  });

  return hasData ? urineData : null;
};

/**
 * Helper: ดึงข้อมูล Stool Test ทั้งหมด
 */
export const getStoolTestData = (labData) => {
  const stoolParams = [
    'stool_color', 'stool_consistency', 'stool_rbc',
    'stool_wbc', 'stool_parasite', 'stool_occult_blood'
  ];

  const stoolData = {};
  let hasData = false;

  stoolParams.forEach(param => {
    const value = getLatestValue(labData, param);
    if (value) {
      stoolData[param] = value.value;
      hasData = true;
    }
  });

  return hasData ? stoolData : null;
};

export default {
  getMemberLabData,
  getLatestValue,
  getHistory,
  getBloodPressure,
  getUrineTestData,
  getStoolTestData
};
